import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Check, Search } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { localized } from "@/i18n";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";

const EMPTY = {
  slug: "", title: { fr: "", en: "", es: "" }, subtitle: { fr: "", en: "", es: "" }, badge: { fr: "", en: "", es: "" },
  cover_image: "", place_ids: [], order: 0,
};

function Field({ label, children }) {
  return <label className="block"><span className="mb-1 block text-xs font-semibold text-ink">{label}</span>{children}</label>;
}
const inputCls = "w-full rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none focus:border-terracotta";
const slugify = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export default function AdminCollections({ places }) {
  const { t, lang } = useApp();
  const { toast } = useToast();
  const [cols, setCols] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [loadError, setLoadError] = useState(false);
  const [formError, setFormError] = useState("");

  const load = () => api.get("/collections").then((r) => { setCols(r.data); setLoadError(false); }).catch(() => setLoadError(true));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setFormError(""); setSearch(""); setForm({ ...EMPTY, order: cols.length + 1 }); setOpen(true); };
  const openEdit = (c) => { setEditing(c.id); setFormError(""); setSearch(""); setForm({ ...EMPTY, ...c }); setOpen(true); };

  const setLoc = (key, l, v) => setForm((f) => ({ ...f, [key]: { ...f[key], [l]: v } }));
  const togglePlace = (id) => setForm((f) => ({ ...f, place_ids: f.place_ids.includes(id) ? f.place_ids.filter((x) => x !== id) : [...f.place_ids, id] }));

  const save = async () => {
    setSaving(true);
    setFormError("");
    const payload = { ...form, order: Number(form.order) || 0, slug: form.slug || slugify(form.title.fr) };
    try {
      if (editing) await api.put(`/admin/collections/${editing}`, payload);
      else await api.post("/admin/collections", payload);
      toast({ title: t("saved") });
      setOpen(false); load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setFormError(typeof detail === "string" ? detail : t("retry"));
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("collection_delete_confirm"))) return;
    try { await api.delete(`/admin/collections/${id}`); load(); } catch { setLoadError(true); }
  };

  const filtered = places.filter((p) => !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.neighborhood?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p data-testid="admin-collection-count" className="text-sm text-ink-muted">{cols.length} {t("admin_collections").toLowerCase()}</p>
        <button data-testid="admin-add-collection" onClick={openNew} className="inline-flex items-center gap-1.5 rounded-full bg-terracotta px-3.5 py-2 text-sm font-bold text-white"><Plus className="h-4 w-4" /> {t("add_collection")}</button>
      </div>

      {loadError && <button data-testid="admin-collections-retry" onClick={load} className="text-sm text-red-600 underline">{t("retry")}</button>}

      <div className="space-y-2">
        {cols.map((c) => (
          <div key={c.id} data-testid={`admin-col-row-${c.id}`} className="flex items-center gap-3 rounded-xl border border-border bg-white p-3">
            <div className="h-12 w-12 shrink-0 overflow-hidden rounded-lg bg-sand">{c.cover_image && <img src={c.cover_image} alt="" className="h-full w-full object-cover" />}</div>
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold text-ink">{localized(c.title, lang)}</p>
              <p className="truncate text-xs text-ink-muted">/{c.slug} · {c.place_ids.length} {t("cluster_places")} · #{c.order}</p>
            </div>
            <button data-testid={`admin-col-edit-${c.id}`} onClick={() => openEdit(c)} className="grid h-9 w-9 place-items-center rounded-full text-sea hover:bg-sea-light"><Pencil className="h-4 w-4" /></button>
            <button data-testid={`admin-col-delete-${c.id}`} onClick={() => remove(c.id)} className="grid h-9 w-9 place-items-center rounded-full text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[88vh] max-w-lg overflow-y-auto rounded-2xl" data-testid="admin-collection-form">
          <DialogHeader><DialogTitle className="font-heading">{editing ? t("edit") : t("add_collection")}</DialogTitle><DialogDescription className="sr-only">{t("admin_collections")}</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <Field label="Titre (FR)"><input data-testid="admin-col-title-fr" className={inputCls} value={form.title.fr} onChange={(e) => setLoc("title", "fr", e.target.value)} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Title (EN)"><input data-testid="admin-col-title-en" className={inputCls} value={form.title.en} onChange={(e) => setLoc("title", "en", e.target.value)} /></Field>
              <Field label="Título (ES)"><input data-testid="admin-col-title-es" className={inputCls} value={form.title.es} onChange={(e) => setLoc("title", "es", e.target.value)} /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Slug (URL)"><input data-testid="admin-col-slug" className={inputCls} placeholder={slugify(form.title.fr) || "valence-en-48h"} value={form.slug} onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })} /></Field>
              <Field label="Ordre"><input data-testid="admin-col-order" type="number" className={inputCls} value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} /></Field>
            </div>
            <Field label="Sous-titre (FR)"><input data-testid="admin-col-subtitle-fr" className={inputCls} value={form.subtitle.fr} onChange={(e) => setLoc("subtitle", "fr", e.target.value)} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Subtitle (EN)"><input data-testid="admin-col-subtitle-en" className={inputCls} value={form.subtitle.en} onChange={(e) => setLoc("subtitle", "en", e.target.value)} /></Field>
              <Field label="Subtítulo (ES)"><input data-testid="admin-col-subtitle-es" className={inputCls} value={form.subtitle.es} onChange={(e) => setLoc("subtitle", "es", e.target.value)} /></Field>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Badge (FR)"><input data-testid="admin-col-badge-fr" className={inputCls} value={form.badge.fr} onChange={(e) => setLoc("badge", "fr", e.target.value)} /></Field>
              <Field label="Badge (EN)"><input data-testid="admin-col-badge-en" className={inputCls} value={form.badge.en} onChange={(e) => setLoc("badge", "en", e.target.value)} /></Field>
              <Field label="Badge (ES)"><input data-testid="admin-col-badge-es" className={inputCls} value={form.badge.es} onChange={(e) => setLoc("badge", "es", e.target.value)} /></Field>
            </div>
            <Field label="Image de couverture (URL)"><input data-testid="admin-col-cover" className={inputCls} value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} /></Field>

            <div>
              <div className="mb-1 flex items-center justify-between">
                <span className="text-xs font-semibold text-ink">{t("pick_places")} ({form.place_ids.length})</span>
              </div>
              <div className="relative mb-2">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
                <input data-testid="admin-col-place-search" className={`${inputCls} pl-8`} placeholder="Rechercher un lieu…" value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
              <div className="max-h-56 space-y-1 overflow-y-auto rounded-lg border border-border p-1">
                {filtered.map((p) => {
                  const on = form.place_ids.includes(p.id);
                  return (
                    <button key={p.id} type="button" data-testid={`admin-col-place-${p.id}`} onClick={() => togglePlace(p.id)} className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm ${on ? "bg-terracotta-light text-terracotta font-semibold" : "text-ink hover:bg-sand"}`}>
                      <span className={`grid h-4 w-4 shrink-0 place-items-center rounded border ${on ? "border-terracotta bg-terracotta text-white" : "border-border"}`}>{on && <Check className="h-3 w-3" />}</span>
                      <span className="truncate">{p.name}</span>
                      <span className="ml-auto shrink-0 text-xs text-ink-muted">{p.neighborhood}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {formError && <p data-testid="admin-col-error" role="alert" className="text-sm text-red-600">{formError}</p>}
            <button data-testid="admin-save-collection" disabled={saving || !form.title.fr.trim()} onClick={save} className="w-full rounded-xl bg-terracotta py-3 text-sm font-bold text-white disabled:opacity-60">{t("save_collection")}</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
